import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
    AlertTriangle,
    RefreshCw,
    Plus,
    Edit,
    Trash2,
    Coffee,
    UtensilsCrossed,
    Croissant,
    GlassWater,
    Grid3X3,
    List,
    Check,
    X,
    AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const menuCategories = [
    { id: 'hot_drinks', label: 'Hot Drinks', icon: Coffee },
    { id: 'cold_drinks', label: 'Cold Drinks', icon: GlassWater },
    { id: 'food', label: 'Food', icon: UtensilsCrossed },
    { id: 'pastries', label: 'Pastries & Cakes', icon: Croissant }
];

const Allergens = () => {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [allergens, setAllergens] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [matrix, setMatrix] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('menu');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    
    // Form state
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [selectedAllergens, setSelectedAllergens] = useState([]);
    const [mayContain, setMayContain] = useState([]);
    const [isAvailable, setIsAvailable] = useState(true);

    const getToken = () => localStorage.getItem('token');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const headers = { Authorization: `Bearer ${getToken()}` };
            const [allergensRes, menuRes, matrixRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/allergens`, { headers }),
                axios.get(`${BACKEND_URL}/api/menu-items`, { headers }),
                axios.get(`${BACKEND_URL}/api/allergen-matrix`, { headers })
            ]);
            setAllergens(allergensRes.data);
            setMenuItems(menuRes.data);
            setMatrix(matrixRes.data);
        } catch (error) {
            console.error('Failed to load allergen data:', error);
            toast.error('Failed to load allergen data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setCategory('');
        setDescription('');
        setSelectedAllergens([]);
        setMayContain([]);
        setIsAvailable(true);
        setEditingItem(null);
    };

    const openEditDialog = (item) => {
        setEditingItem(item);
        setName(item.name);
        setCategory(item.category);
        setDescription(item.description || '');
        setSelectedAllergens(item.allergens || []);
        setMayContain(item.may_contain || []);
        setIsAvailable(item.is_available);
        setDialogOpen(true);
    };

    const handleAllergenToggle = (allergenId, list, setList) => {
        setList(prev => 
            prev.includes(allergenId)
                ? prev.filter(id => id !== allergenId)
                : [...prev, allergenId]
        );
    };

    const handleSubmit = async () => {
        if (!name || !category) {
            toast.error('Please fill in name and category');
            return;
        }

        const headers = { Authorization: `Bearer ${getToken()}` };
        const itemData = {
            name,
            category,
            description: description || null,
            allergens: selectedAllergens,
            may_contain: mayContain,
            is_available: isAvailable
        };

        try {
            if (editingItem) {
                await axios.put(`${BACKEND_URL}/api/menu-items/${editingItem.id}`, itemData, { headers });
                toast.success('Menu item updated successfully');
            } else {
                await axios.post(`${BACKEND_URL}/api/menu-items`, itemData, { headers });
                toast.success('Menu item added successfully');
            }
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            console.error('Failed to save menu item:', error);
            toast.error('Failed to save menu item');
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;

        try {
            const headers = { Authorization: `Bearer ${getToken()}` };
            await axios.delete(`${BACKEND_URL}/api/menu-items/${item.id}`, { headers });
            toast.success('Menu item deleted');
            loadData();
        } catch (error) {
            console.error('Failed to delete menu item:', error);
            toast.error('Failed to delete menu item');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="allergens-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Allergen Management</h1>
                        <p className="text-slate-400 mt-1">
                            Manage menu items and allergen information (14 major allergens)
                        </p>
                    </div>
                    {isManager && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button className="brand-btn" data-testid="add-menu-item-button">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Menu Item
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="text-slate-100">
                                        {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
                                    </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Name *</Label>
                                            <Input
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="e.g., Cappuccino"
                                                className="bg-slate-950 border-slate-800 text-slate-200"
                                                data-testid="menu-item-name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Category *</Label>
                                            <Select value={category} onValueChange={setCategory}>
                                                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="menu-item-category">
                                                    <SelectValue placeholder="Select category" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {menuCategories.map(cat => (
                                                        <SelectItem key={cat.id} value={cat.id} className="text-slate-200 focus:bg-slate-800">
                                                            {cat.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Description</Label>
                                        <Textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Brief description..."
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-slate-300 flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-rose-400" />
                                            Contains Allergens
                                        </Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-slate-950 rounded-lg border border-slate-800 max-h-48 overflow-y-auto">
                                            {allergens.map(allergen => (
                                                <div key={allergen.id} className="flex items-center gap-2">
                                                    <Checkbox
                                                        id={`allergen-${allergen.id}`}
                                                        checked={selectedAllergens.includes(allergen.id)}
                                                        onCheckedChange={() => handleAllergenToggle(allergen.id, selectedAllergens, setSelectedAllergens)}
                                                        className="border-rose-500 data-[state=checked]:bg-rose-500"
                                                    />
                                                    <label htmlFor={`allergen-${allergen.id}`} className="text-sm text-slate-300 cursor-pointer flex items-center gap-1">
                                                        <span>{allergen.icon}</span>
                                                        {allergen.name}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-slate-300 flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 brand-icon" />
                                            May Contain (Cross-contamination)
                                        </Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3 bg-slate-950 rounded-lg border border-slate-800 max-h-48 overflow-y-auto">
                                            {allergens.map(allergen => (
                                                <div key={`may-${allergen.id}`} className="flex items-center gap-2">
                                                    <Checkbox
                                                        id={`may-contain-${allergen.id}`}
                                                        checked={mayContain.includes(allergen.id)}
                                                        onCheckedChange={() => handleAllergenToggle(allergen.id, mayContain, setMayContain)}
                                                        disabled={selectedAllergens.includes(allergen.id)}
                                                        className="border-[var(--brand-primary)] data-[state=checked]:bg-[var(--brand-primary)]"
                                                    />
                                                    <label htmlFor={`may-contain-${allergen.id}`} className={cn(
                                                        "text-sm cursor-pointer flex items-center gap-1",
                                                        selectedAllergens.includes(allergen.id) ? "text-slate-600" : "text-slate-300"
                                                    )}>
                                                        <span>{allergen.icon}</span>
                                                        {allergen.name}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                                        <div>
                                            <Label className="text-slate-300">Available</Label>
                                            <p className="text-xs text-slate-500">Show on allergen matrix</p>
                                        </div>
                                        <Switch
                                            checked={isAvailable}
                                            onCheckedChange={setIsAvailable}
                                        />
                                    </div>

                                    <Button
                                        onClick={handleSubmit}
                                        className="w-full brand-btn"
                                        data-testid="save-menu-item"
                                    >
                                        {editingItem ? 'Update Menu Item' : 'Add Menu Item'}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Info Banner */}
                <Card className="bg-amber-500/10 border-amber-500/20">
                    <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
                            <div>
                                <p className="text-amber-200 font-medium">14 Major Allergens (UK Food Regulations)</p>
                                <p className="text-amber-300/70 text-sm mt-1">
                                    By law, you must tell customers if any food you sell contains any of the 14 allergens.
                                    This information must be provided in writing if requested.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="bg-slate-900 border border-slate-800">
                        <TabsTrigger value="menu" className="brand-btn data-[state=active]:text-white">
                            <List className="h-4 w-4 mr-2" />
                            Menu Items
                        </TabsTrigger>
                        <TabsTrigger value="matrix" className="brand-btn data-[state=active]:text-white">
                            <Grid3X3 className="h-4 w-4 mr-2" />
                            Allergen Matrix
                        </TabsTrigger>
                        <TabsTrigger value="allergens" className="brand-btn data-[state=active]:text-white">
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            Allergen Guide
                        </TabsTrigger>
                    </TabsList>

                    {/* Menu Items Tab */}
                    <TabsContent value="menu" className="mt-6">
                        {menuItems.length > 0 ? (
                            <div className="space-y-6">
                                {menuCategories.map(cat => {
                                    const items = menuItems.filter(i => i.category === cat.id);
                                    if (items.length === 0) return null;
                                    
                                    const Icon = cat.icon;
                                    return (
                                        <div key={cat.id}>
                                            <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
                                                <Icon className="h-5 w-5 brand-icon" />
                                                {cat.label}
                                            </h3>
                                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {items.map(item => (
                                                    <Card key={item.id} className={cn(
                                                        "bg-slate-900 border-slate-800",
                                                        !item.is_available && "opacity-50"
                                                    )} data-testid={`menu-item-${item.id}`}>
                                                        <CardContent className="pt-4">
                                                            <div className="flex items-start justify-between mb-3">
                                                                <div>
                                                                    <h4 className="font-medium text-slate-200">{item.name}</h4>
                                                                    {item.description && (
                                                                        <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                                                                    )}
                                                                </div>
                                                                {!item.is_available && (
                                                                    <Badge variant="outline" className="border-slate-600 text-slate-500">
                                                                        Unavailable
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            
                                                            {item.allergens?.length > 0 && (
                                                                <div className="mb-2">
                                                                    <p className="text-xs text-rose-400 mb-1 font-medium">Contains:</p>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {item.allergens.map(a => {
                                                                            const allergen = allergens.find(al => al.id === a);
                                                                            return allergen ? (
                                                                                <Badge key={a} className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs">
                                                                                    {allergen.icon} {allergen.name}
                                                                                </Badge>
                                                                            ) : null;
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {item.may_contain?.length > 0 && (
                                                                <div className="mb-2">
                                                                    <p className="text-xs text-amber-400 mb-1 font-medium">May contain:</p>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {item.may_contain.map(a => {
                                                                            const allergen = allergens.find(al => al.id === a);
                                                                            return allergen ? (
                                                                                <Badge key={a} className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                                                                                    {allergen.icon} {allergen.name}
                                                                                </Badge>
                                                                            ) : null;
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {item.allergens?.length === 0 && item.may_contain?.length === 0 && (
                                                                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                                                    No major allergens
                                                                </Badge>
                                                            )}

                                                            {isManager && (
                                                                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-800">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => openEditDialog(item)}
                                                                        className="flex-1 text-slate-400 hover:text-slate-200"
                                                                    >
                                                                        <Edit className="h-4 w-4 mr-1" />
                                                                        Edit
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDelete(item)}
                                                                        className="text-slate-400 hover:text-rose-400"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="py-12">
                                    <div className="empty-state">
                                        <UtensilsCrossed className="empty-state-icon" />
                                        <p className="empty-state-title">No menu items</p>
                                        <p className="empty-state-description">
                                            Add menu items to track allergen information.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Allergen Matrix Tab */}
                    <TabsContent value="matrix" className="mt-6">
                        {matrix && matrix.items.length > 0 ? (
                            <Card className="bg-slate-900 border-slate-800 overflow-hidden">
                                <CardHeader>
                                    <CardTitle className="text-lg text-slate-100">Allergen Matrix</CardTitle>
                                    <CardDescription>
                                        <span className="inline-flex items-center gap-4 mt-2">
                                            <span className="flex items-center gap-1">
                                                <div className="w-4 h-4 bg-rose-500 rounded" /> Contains
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <div className="w-4 h-4 bg-amber-500 rounded" /> May contain
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <div className="w-4 h-4 bg-slate-700 rounded" /> Free
                                            </span>
                                        </span>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-800">
                                                <th className="text-left py-3 px-2 text-slate-400 font-medium sticky left-0 bg-slate-900 z-10">
                                                    Item
                                                </th>
                                                {matrix.allergens.map(a => (
                                                    <th key={a.id} className="text-center py-3 px-1 text-slate-400 font-medium min-w-[40px]" title={a.name}>
                                                        <span className="text-lg">{a.icon}</span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matrix.items.map((item, idx) => (
                                                <tr key={item.id} className={cn(
                                                    "border-b border-slate-800/50",
                                                    idx % 2 === 0 ? "bg-slate-900" : "bg-slate-800/30"
                                                )}>
                                                    <td className="py-2 px-2 text-slate-200 sticky left-0 bg-inherit z-10">
                                                        {item.name}
                                                    </td>
                                                    {matrix.allergens.map(a => {
                                                        const status = item.allergens[a.id];
                                                        return (
                                                            <td key={a.id} className="text-center py-2 px-1">
                                                                {status === 'contains' && (
                                                                    <div className="w-6 h-6 mx-auto rounded bg-rose-500 flex items-center justify-center">
                                                                        <X className="h-4 w-4 text-white" />
                                                                    </div>
                                                                )}
                                                                {status === 'may_contain' && (
                                                                    <div className="w-6 h-6 mx-auto rounded bg-amber-500 flex items-center justify-center">
                                                                        <AlertCircle className="h-4 w-4 text-white" />
                                                                    </div>
                                                                )}
                                                                {status === 'free' && (
                                                                    <div className="w-6 h-6 mx-auto rounded bg-slate-700 flex items-center justify-center">
                                                                        <Check className="h-4 w-4 text-emerald-400" />
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="py-12">
                                    <div className="empty-state">
                                        <Grid3X3 className="empty-state-icon" />
                                        <p className="empty-state-title">No matrix data</p>
                                        <p className="empty-state-description">
                                            Add menu items to generate the allergen matrix.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Allergen Guide Tab */}
                    <TabsContent value="allergens" className="mt-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            {allergens.map(allergen => (
                                <Card key={allergen.id} className="bg-slate-900 border-slate-800">
                                    <CardContent className="pt-4">
                                        <div className="flex items-start gap-4">
                                            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-2xl">
                                                {allergen.icon}
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-slate-200">{allergen.name}</h4>
                                                <p className="text-sm text-slate-400 mt-1">{allergen.description}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
};

export default Allergens;
